import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Activity, AlertTriangle, Calendar, Heart } from "lucide-react";
import { motion } from "framer-motion";

export interface PatientSummary {
  id: string;
  full_name: string;
  avatar_url?: string | null;
  data_nascimento?: string | null;
  sexo?: string | null;
  conditions?: string[];
  healthScore?: number;
  lastRecord?: {
    title: string;
    date: string;
  };
  alerts?: string[];
  aiRecommendations?: string[];
}

interface PatientSummaryCardProps {
  patient: PatientSummary | null;
}

export default function PatientSummaryCard({ patient }: PatientSummaryCardProps) {
  if (!patient) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <Activity className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              Selecione um aluno para ver o resumo
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const age = patient.data_nascimento
    ? Math.floor(
        (new Date().getTime() - new Date(patient.data_nascimento).getTime()) /
          (1000 * 60 * 60 * 24 * 365)
      )
    : null;

  const initials = patient.full_name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 border-pink-200 dark:border-pink-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Heart className="h-5 w-5 text-pink-500" />
            Resumo do Aluno
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Perfil */}
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16 border-2 border-pink-300 dark:border-pink-700">
              <AvatarImage src={patient.avatar_url} alt={patient.full_name} />
              <AvatarFallback className="bg-pink-100 dark:bg-pink-900 text-pink-700 dark:text-pink-300 text-lg">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{patient.full_name}</h3>
              {age && (
                <p className="text-sm text-muted-foreground">{age} anos</p>
              )}
            </div>
            {patient.healthScore && (
              <div className="text-right">
                <div className="text-2xl font-bold text-pink-600 dark:text-pink-400">
                  {patient.healthScore}
                </div>
                <p className="text-xs text-muted-foreground">Score Saúde</p>
              </div>
            )}
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-pink-300 dark:via-pink-700 to-transparent" />

          {/* Condições */}
          {patient.conditions && patient.conditions.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-4 w-4 text-pink-500" />
                <span className="text-sm font-medium">Condições:</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {patient.conditions.map((condition, idx) => (
                  <Badge
                    key={idx}
                    variant="secondary"
                    className="bg-pink-100 dark:bg-pink-900/50 text-pink-700 dark:text-pink-300"
                  >
                    {condition}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Último registro */}
          {patient.lastRecord && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-pink-500" />
                <span className="text-sm font-medium">Último registro:</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {patient.lastRecord.title} •{" "}
                {new Date(patient.lastRecord.date).toLocaleDateString("pt-BR")}
              </p>
            </div>
          )}

          {/* Alertas */}
          {patient.alerts && patient.alerts.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-medium">Alertas:</span>
              </div>
              <ul className="space-y-1">
                {patient.alerts.map((alert, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2"
                  >
                    <span className="text-amber-500 mt-0.5">•</span>
                    {alert}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recomendações IA */}
          {patient.aiRecommendations && patient.aiRecommendations.length > 0 && (
            <div className="bg-gradient-to-r from-pink-100 to-rose-100 dark:from-pink-900/30 dark:to-rose-900/30 rounded-lg p-3">
              <div className="flex items-start gap-2 mb-2">
                <div className="p-1 bg-pink-200 dark:bg-pink-800 rounded">
                  <Activity className="h-3.5 w-3.5 text-pink-600 dark:text-pink-300" />
                </div>
                <span className="text-sm font-medium">IA recomenda:</span>
              </div>
              <ul className="space-y-1">
                {patient.aiRecommendations.map((rec, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-muted-foreground flex items-start gap-2"
                  >
                    <span className="text-pink-500 mt-0.5">→</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
